import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

import { MintExchangeAvailableCurrency } from '@flipster/apis/v1/earn/types';
import { CurrencyDisplay } from '@flipster/features/src/misc/display';
import { useDepositModal } from '@flipster/features/src/shared/wallet-modal/deposit';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { Button } from '@flipster/react/design-system/button';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { AssetPrecision } from '@flipster/utils/constants/currency';
import Num, { numberFormatter } from '@flipster/utils/num';

import { mintBunja } from './state';

const DepositGuide = ({ onClose }: { onClose: () => void }) => {
  const t = useTranslations();
  const openDepositModal = useDepositModal();
  const { providerCurrencyAtom, collateralCurrenciesAtom } = useBunja(mintBunja);
  const providerCurrency = useAtomValue(providerCurrencyAtom);
  const collateralCurrencies = useAtomValue(collateralCurrenciesAtom);

  const { walletsAtom } = useBunja(walletsBunja);
  const wallets = useAtomValue(walletsAtom);

  const supportedCurrencies = useMemo(
    () =>
      collateralCurrencies.map((currencyInfo) => {
        const balance = Num(wallets?.[currencyInfo.currency]?.walletBalanceExclLocked ?? '0');

        return {
          ...currencyInfo,
          balance: balance.toFixed(AssetPrecision),
        };
      }),
    [collateralCurrencies, wallets],
  );

  const openDeposit = (currency: string) => {
    onClose();
    openDepositModal({ defaultValues: { currency } });
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title label={t('Mint.r251008.Deposit_to_mint_quote_currency', { quoteCurrency: providerCurrency })} />
      </Modal.Header>
      <Modal.Content>
        <div className={stack({ gap: 16 })}>
          {supportedCurrencies.map((currencyInfo) => (
            <DepositCurrencyItem key={currencyInfo.currency} currencyInfo={currencyInfo} onClick={openDeposit} />
          ))}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button
          label={t('Common.r251008.Not_now')}
          hierarchy='secondary'
          size='large'
          color='neutral'
          onClick={onClose}
        />
      </Modal.Footer>
    </>
  );
};

export default DepositGuide;

const DepositCurrencyItem = ({
  currencyInfo,
  onClick,
}: {
  currencyInfo: MintExchangeAvailableCurrency & { balance: string };
  onClick: (currency: string) => void;
}) => {
  const t = useTranslations();

  return (
    <div
      className={hstack({
        justify: 'space-between',
        p: 16,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.default.level-1',
        rounded: 8,
      })}
    >
      <div className={stack({ gap: 8 })}>
        <div>
          <CurrencyDisplay
            currency={currencyInfo.currency}
            icon={<CurrencyDisplay.Icon size={24} space={8} />}
            mainLabel={
              <CurrencyDisplay.Label
                value='shortName'
                css={{ textStyle: 'Text/M/Regular', color: 'content.default.level-5' }}
              />
            }
          />
        </div>
        <div>
          <Formatted.Number
            value={currencyInfo.balance}
            suffix={currencyInfo.currency}
            precision={AssetPrecision}
            toFixed
            css={{ textStyle: 'Text/L/Monospace/Strong', color: 'content.default.level-5' }}
          />
        </div>
        <p className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-3', pt: 6 })}>
          {t('Mint.r251008.Min_amount_colon_amount_quote_currency', {
            amountQuoteCurrency: `${numberFormatter(currencyInfo.minAmount, { withComma: true })} ${currencyInfo.currency}`,
          })}
        </p>
      </div>
      <div>
        <Button
          label={t('Navigation.r250401.Deposit')}
          hierarchy='primary'
          color='accent'
          size='medium'
          onClick={() => onClick(currencyInfo.currency)}
        />
      </div>
    </div>
  );
};
